import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { FC, useContext, useMemo, useState, type JSX } from 'react'

import { useGuide } from '@context/FirstTimeGuideContext'
import { useProductReadContext } from '@context/ProductContext'

import { useApiClient } from '@utils/ApiClient'
import { SharedContext } from '@utils/SharedContext'
import { ClientApiClient } from '@utils/clientApi'
import { CommonPaths } from '@utils/path'

import { isTickmillProductTypeCFD, isTickmillProductTypeETD } from '@model/TickmillProductType'
import { WalletTypeEnum, isWalletETDType, isWalletFX_CFDType } from '@model/WalletDto'

import { useFundingNav } from '@hooks/useFundingNav'

import { Loading } from '@shared/components/Loading/Loading'

import { useDepositTracker } from '@analytics/amplitude/deposit'

import { FirstTimeGuideCarouselCFD } from './FirstTimeGuideCarouselCFD'
import { FirstTimeGuideCarouselETD } from './FirstTimeGuideCarouselETD'

interface FirstTimeGuideCarouselProps {
  walletType?: WalletTypeEnum
  disabled?: boolean
}

export const FirstTimeGuideCarousel: FC<FirstTimeGuideCarouselProps> = ({
  walletType,
  disabled,
}): JSX.Element => {
  const { product, isDefaultCFDProductType } = useProductReadContext()
  const { guide, fetchGuide, isLoading } = useGuide(disabled)
  const [sharedState, setSharedState] = useContext(SharedContext)
  const [isWalletsLoading, setIsWalletsLoading] = useState(false)
  const tracker = useDepositTracker()

  const allWalletsDisabled = useMemo(() => {
    return sharedState.wallets.every((wallet) => !wallet.depositAction.canDeposit)
  }, [sharedState.wallets])

  const apiClient = useApiClient(ClientApiClient)

  const { navigateToFunding } = useFundingNav()

  const walletTypeId = walletType ?? WalletTypeEnum.CFD

  const { data: wallets = [] } = useQuery({
    queryKey: queryKeys.wallet.byType(walletTypeId),
    queryFn: async () => {
      setIsWalletsLoading(true)

      try {
        const walletTypes = sharedState.walletTypes.length
          ? sharedState.walletTypes
          : await apiClient.getWalletTypes()

        const response = await apiClient.getWallets({ walletTypeId })

        const filteredWallets = response
          .filter((x) =>
            isDefaultCFDProductType()
              ? isWalletFX_CFDType(x.walletType.id)
              : isWalletETDType(x.walletType.id)
          )
          .map(({ id, name, currency, depositAction }) => ({ id, name, currency, depositAction }))

        setSharedState((currentState) => ({
          ...currentState,
          walletTypes,
          wallets: filteredWallets,
        }))

        return response
      } finally {
        setIsWalletsLoading(false)
      }
    },
    enabled: !disabled && !!guide?.shouldShow,
  })

  const handleDeposit = () => {
    if (wallets.length === 1) {
      tracker.start({ entry_point: 'getting_started_slider', preset_wallet_id: wallets[0].id })
      navigateToFunding(`${CommonPaths.Deposit}/wallet`, {
        state: { selectedWalletId: wallets[0].id },
      })
    } else {
      tracker.start({ entry_point: 'getting_started_slider' })
      navigateToFunding(`${CommonPaths.Deposit}/wallet`)
    }
  }

  if (disabled || !guide?.shouldShow) return <></>

  return (
    <Loading isLoading={isLoading}>
      {isTickmillProductTypeCFD(product) && (
        <FirstTimeGuideCarouselCFD
          reloadGuide={fetchGuide}
          walletType={walletType}
          depositDisabled={allWalletsDisabled}
          isWalletsLoading={isWalletsLoading}
          handleDeposit={handleDeposit}
        />
      )}
      {isTickmillProductTypeETD(product) && (
        <FirstTimeGuideCarouselETD reloadGuide={fetchGuide} handleDeposit={handleDeposit} />
      )}
    </Loading>
  )
}
